import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import type { DashboardOverview } from '@/lib/types';
import StudentDashboard from '@/components/dashboard/StudentDashboard';
import TutorDashboard from '@/components/dashboard/TutorDashboard';
import AdminDashboard from '@/components/dashboard/AdminDashboard';

export default function DashboardPage() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const loadOverview = async () => {
      try {
        setLoadingOverview(true);
        setError(null);
        const response = await apiClient.getDashboardOverview();
        if (cancelled) return;

        if (response.success && response.data) {
          setOverview(response.data);
        } else {
          setError(response.error || 'Failed to load dashboard');
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load dashboard');
        }
      } finally {
        if (!cancelled) {
          setLoadingOverview(false);
        }
      }
    };

    loadOverview();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || 'there';

  const roleTitle =
    user?.role === 'student'
      ? 'Student overview'
      : user?.role === 'tutor'
        ? 'Tutor overview'
        : 'Admin overview';

  return (
    <div className="min-h-full">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <p className="text-sm font-medium uppercase tracking-wide text-amber-700">Dashboard</p>
          <h1 className="mt-1 text-xl font-bold tracking-tight text-slate-900">{roleTitle}</h1>
          <p className="mt-2 text-sm text-slate-600">
            Welcome back, {displayName}. Here&apos;s a snapshot of your Phino LMS activity.
          </p>
        </div>

        {loadingOverview && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}

        {!loadingOverview && error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-red-800">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-2 text-sm font-medium text-red-700 underline hover:text-red-900"
            >
              Try again
            </button>
          </div>
        )}

        {!loadingOverview && overview && user?.role === 'student' && (
          <StudentDashboard data={overview} />
        )}
        {!loadingOverview && overview && user?.role === 'tutor' && (
          <TutorDashboard data={overview} />
        )}
        {!loadingOverview && overview && user?.role === 'admin' && (
          <AdminDashboard data={overview} />
        )}
      </div>
    </div>
  );
}
